import { AxiosError, isAxiosError } from 'axios';

import { handleAndMonitorError } from '../helpers';

export const NON_RETRYABLE_STATUS_CODES = [401, 403] as const;

const formatErrorData = (data: unknown, fallback: string): string => {
  if (data === undefined || data === null) return fallback;
  if (typeof data === 'object') return JSON.stringify(data);
  return String(data);
};

export interface ErrorContext {
  component?: string;
  action?: string;
  sessionId?: string;
  chargePointId?: number;
  evseId?: number;
  userId?: string;
  additionalData?: Record<string, unknown>;
}

export interface ErrorRecoveryOptions {
  retry?: {
    enabled: boolean;
    maxAttempts: number;
    delay: number;
    backoff?: 'linear' | 'exponential';
  };
  fallback?: () => void;
  userMessage?: string;
  redirect?: string;
}

export interface ProcessedError {
  type: 'network' | 'validation' | 'auth' | 'session' | 'payment' | 'system' | 'unknown';
  severity: 'low' | 'medium' | 'high' | 'critical';
  userMessage: string;
  technicalMessage: string;
  context: ErrorContext;
  canRetry: boolean;
  shouldReportToUser: boolean;
  recoveryOptions?: ErrorRecoveryOptions;
}

export class EnhancedErrorHandler {
  private static retryAttempts = new Map<string, number>();

  static async processError(
    error: unknown,
    context: ErrorContext = {},
    recoveryOptions?: ErrorRecoveryOptions
  ): Promise<ProcessedError> {
    const processed = this.categorizeError(error, context);

    // Add recovery options
    if (recoveryOptions) {
      processed.recoveryOptions = recoveryOptions;
    }

    // Report to monitoring
    this.reportError(processed);

    return processed;
  }

  private static categorizeError(error: unknown, context: ErrorContext): ProcessedError {
    const base: ProcessedError = {
      type: 'unknown',
      severity: 'medium',
      userMessage: 'An unexpected error occurred',
      technicalMessage: String(error),
      context,
      canRetry: false,
      shouldReportToUser: true,
    };

    if (error instanceof AxiosError) {
      return this.processAxiosError(error, context);
    }

    if (error instanceof Error) {
      return this.processJavaScriptError(error, context);
    }

    if (typeof error === 'string') {
      return {
        ...base,
        technicalMessage: error,
      };
    }

    return base;
  }

  private static processAxiosError(error: AxiosError, context: ErrorContext): ProcessedError {
    const status = error.response?.status;
    const errorCode = error.code;

    // Network errors
    if (errorCode === 'ERR_NETWORK' || errorCode === 'ECONNABORTED' || !status) {
      return {
        type: 'network',
        severity: 'high',
        userMessage: 'Connection problem. Please check your internet connection and try again.',
        technicalMessage: `Network error: ${error.message}`,
        context,
        canRetry: true,
        shouldReportToUser: true,
      };
    }

    // Authentication errors
    if (status === 401) {
      return {
        type: 'auth',
        severity: 'high',
        userMessage: 'Session expired or invalid credentials. Please try again.',
        technicalMessage: `Unauthorized: ${formatErrorData(error.response?.data, error.message)}`,
        context,
        canRetry: false,
        shouldReportToUser: true,
      };
    }

    // Validation errors
    if (status === 400) {
      return {
        type: 'validation',
        severity: 'medium',
        userMessage: 'Invalid input. Please check your information and try again.',
        technicalMessage: `Bad request: ${formatErrorData(error.response?.data, error.message)}`,
        context,
        canRetry: false,
        shouldReportToUser: true,
      };
    }

    // Session not found
    if (status === 404 && context.sessionId) {
      return {
        type: 'session',
        severity: 'high',
        userMessage: 'Session not found. Please start a new session.',
        technicalMessage: `Session not found: ${formatErrorData(error.response?.data, error.message)}`,
        context,
        canRetry: false,
        shouldReportToUser: true,
      };
    }

    // Payment errors
    if (status === 402 || (status === 400 && context.action?.includes('payment'))) {
      return {
        type: 'payment',
        severity: 'critical',
        userMessage: 'Payment processing failed. Please try again or use a different payment method.',
        technicalMessage: `Payment error: ${formatErrorData(error.response?.data, error.message)}`,
        context,
        canRetry: true,
        shouldReportToUser: true,
      };
    }

    // Server errors
    if (status && status >= 500) {
      return {
        type: 'system',
        severity: 'critical',
        userMessage: 'Server error. Please try again in a few moments.',
        technicalMessage: `Server error ${status}: ${formatErrorData(error.response?.data, error.message)}`,
        context,
        canRetry: true,
        shouldReportToUser: true,
      };
    }

    // Rate limiting
    if (status === 429) {
      return {
        type: 'system',
        severity: 'medium',
        userMessage: 'Too many requests. Please wait a moment and try again.',
        technicalMessage: `Rate limited: ${formatErrorData(error.response?.data, error.message)}`,
        context,
        canRetry: true,
        shouldReportToUser: true,
      };
    }

    return {
      type: 'unknown',
      severity: 'medium',
      userMessage: 'An unexpected error occurred. Please try again.',
      technicalMessage: `HTTP ${status}: ${formatErrorData(error.response?.data, error.message)}`,
      context,
      canRetry: status ? status >= 500 : false,
      shouldReportToUser: true,
    };
  }

  private static processJavaScriptError(error: Error, context: ErrorContext): ProcessedError {
    // Component errors
    if (error.message.includes('ChunkLoadError') || error.message.includes('Loading chunk')) {
      return {
        type: 'system',
        severity: 'medium',
        userMessage: 'Failed to load application resources. Please refresh the page.',
        technicalMessage: `Chunk load error: ${error.message}`,
        context,
        canRetry: false,
        shouldReportToUser: true,
      };
    }

    // Validation errors
    if (error.name === 'ValidationError' || error.message.includes('validation')) {
      return {
        type: 'validation',
        severity: 'low',
        userMessage: 'Please check your input and try again.',
        technicalMessage: error.message,
        context,
        canRetry: false,
        shouldReportToUser: true,
      };
    }

    return {
      type: 'system',
      severity: 'medium',
      userMessage: 'An unexpected error occurred. Please try again.',
      technicalMessage: `${error.name}: ${error.message}`,
      context,
      canRetry: false,
      shouldReportToUser: true,
    };
  }

  private static reportError(processed: ProcessedError): void {
    // Report to Bugsnag with enhanced context
    handleAndMonitorError(new Error(processed.technicalMessage), {
      errorType: processed.type,
      severity: processed.severity,
      context: processed.context,
      canRetry: processed.canRetry,
      userMessage: processed.userMessage,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
  }

  static async retryWithBackoff<T>(
    operation: () => Promise<T>,
    options: {
      maxAttempts: number;
      baseDelay: number;
      backoff: 'linear' | 'exponential';
      operationId?: string;
    }
  ): Promise<T> {
    const { maxAttempts, baseDelay, backoff, operationId = 'unknown' } = options;
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const result = await operation();

        // Reset retry count on success
        if (operationId) {
          this.retryAttempts.delete(operationId);
        }

        return result;
      } catch (error) {
        lastError = error;

        // Update retry count
        if (operationId) {
          this.retryAttempts.set(operationId, attempt);
        }

        // Don't retry non-retryable errors (e.g. 401 Unauthorized, 403 Forbidden)
        if (isAxiosError(error) && NON_RETRYABLE_STATUS_CODES.includes(error.response?.status as any)) {
          break;
        }

        // Don't retry on the last attempt
        if (attempt === maxAttempts) {
          break;
        }

        // Calculate delay
        const delay = backoff === 'exponential' ? baseDelay * Math.pow(2, attempt - 1) : baseDelay * attempt;

        // Add jitter to prevent thundering herd
        const jitteredDelay = delay + Math.random() * 1000;

        await new Promise(resolve => setTimeout(resolve, jitteredDelay));
      }
    }

    throw lastError;
  }

  static getRetryCount(operationId: string): number {
    return this.retryAttempts.get(operationId) || 0;
  }

  static clearRetryCount(operationId: string): void {
    this.retryAttempts.delete(operationId);
  }
}

// Convenience functions for common error scenarios
export const createSessionErrorContext = (
  sessionId?: string,
  chargePointId?: number,
  evseId?: number,
  component?: string,
  action?: string
): ErrorContext => ({
  sessionId,
  chargePointId,
  evseId,
  component,
  action,
});

export const createPaymentErrorContext = (
  paymentIntentId?: string,
  component?: string,
  action?: string
): ErrorContext => ({
  component,
  action,
  additionalData: { paymentIntentId },
});
